import { Avatar, Flex, Popover, Progress, Tooltip, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { cn } from "../../../../utils/cn.ts"
import { readAccountInstant } from "../../../../utils/date/read-account-instant.ts"
import { shortRelativeTime } from "../../../../utils/date/short-relative-time.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name"
import type { ProjectManager } from "../../../Project/types/project.types.ts"
import {
  getProjectCustomerName,
  normalizePercent
} from "../../constants/helpers.ts"
import { isArchivingStatus } from "../../constants/project-statuses.ts"
import {
  HEALTH_DOT_CLASS,
  HEALTH_TEXT_CLASS,
  scheduleLabel,
  scheduleTone
} from "../../constants/schedule-health.ts"
import type { ProjectListItem } from "../../types/types.ts"

const { Text } = Typography

const NOT_STARTED_STATUSES = new Set(["Planning", "Ready to Start"])

export function ProjectIdentityCell({ project }: { project: ProjectListItem }) {
  const customer = getProjectCustomerName(project)
  const type = project.project_type_details?.[0]?.name
  const place = [project.city, project.state].filter(Boolean).join(", ")
  // A missing segment is left out, never shown as a dash between two others.
  const secondary = [customer, type, place].filter(Boolean).join(" · ")

  return (
    <Flex vertical className="min-w-0 text-start">
      <Link
        to={RoutesPaths.project.view(project.id).overview}
        className="font-semibold min-w-0"
      >
        <Text
          className="text-inherit font-semibold"
          ellipsis={{ tooltip: project.lead_name }}
        >
          {project.lead_name}
        </Text>
      </Link>
      {secondary ? (
        <Text
          className="text-slate-600 text-[13px]"
          ellipsis={{ tooltip: secondary }}
        >
          {secondary}
        </Text>
      ) : null}
    </Flex>
  )
}

export function ScheduleHealthCell({
  project,
  withNextMilestone = false
}: {
  project: ProjectListItem
  /** On a narrower table the next milestone rides here as a second line. */
  withNextMilestone?: boolean
}) {
  const { t } = useTranslation()
  const health = project.schedule_health
  if (!health) {
    return <Text type="secondary">{t("projectsCommand.notAvailable")}</Text>
  }

  const tone = scheduleTone(health, project.project_status)
  return (
    <Flex vertical className="min-w-0">
      <Flex align="center" gap={8} className="min-w-0">
        <span
          aria-hidden
          className={cn("size-2 shrink-0 rounded-full", HEALTH_DOT_CLASS[tone])}
        />
        <Text className={cn("min-w-0", HEALTH_TEXT_CLASS[tone])} ellipsis>
          {scheduleLabel(t, health)}
        </Text>
      </Flex>
      {withNextMilestone ? (
        <div className="ps-4 text-[13px] [&_.ant-typography]:!text-[13px] [&_.ant-typography]:!text-slate-600">
          <NextMilestoneCell project={project} />
        </div>
      ) : null}
    </Flex>
  )
}

export function NextMilestoneCell({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation()
  const { formatDate } = useMilestoneDate()
  const next = project.next_milestone

  if (!next) {
    // Progress is null exactly when a project has no milestones, so it tells a
    // schedule never set up from one whose milestones are all finished.
    return (
      <Text type="secondary">
        {normalizeProgress(project) === null
          ? t("projectsCommand.nextMilestone.notConfigured")
          : t("projectsCommand.nextMilestone.none")}
      </Text>
    )
  }

  const due = next.end_date ? formatDate(next.end_date) : null
  const label = [next.name, due].filter(Boolean).join(" · ")
  return (
    <Text className="text-slate-700" ellipsis={{ tooltip: label }}>
      {next.name || t("projectsCommand.nextMilestone.unnamed")}
      {due ? <span className="text-slate-600"> · {due}</span> : null}
    </Text>
  )
}

function useMilestoneDate() {
  const { userFormat } = useDateFormat()
  return {
    // A planned end is a calendar day; formatting it through the account's
    // timezone would move it a day west of UTC.
    formatDate: (value: string) => {
      const day = dayjs(value, "YYYY-MM-DD", true)
      if (!day.isValid()) return value
      return day.year() === dayjs().year()
        ? day.format("MMM D")
        : day.format(userFormat || "MMM D, YYYY")
    }
  }
}

function initialsOf(person: ProjectManager) {
  const name = getEntityName(person, "")
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join("")
}

function PersonAvatar({ person }: { person: ProjectManager }) {
  return (
    <Avatar
      size={28}
      src={person.avatar || undefined}
      className="shrink-0 bg-teal-50 text-teal-800 text-xs font-semibold"
      aria-hidden
    >
      {initialsOf(person) || "?"}
    </Avatar>
  )
}

export function ProjectManagerCell({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation()
  const managers = project.project_managers ?? []
  const main = managers.find((m) => m.is_main) ?? managers[0]
  const others = [
    ...managers
      .filter((m) => m !== main)
      .map((person) => ({ person, role: t("projectsCommand.roles.pm") })),
    ...(project.sales_managers ?? []).map((person) => ({
      person,
      role: t("projectsCommand.roles.salesPm")
    }))
  ]

  if (!main && !others.length) {
    return <Text type="secondary">{t("projectsCommand.unassigned")}</Text>
  }

  const primary = main ?? others[0].person
  const rest = main ? others : others.slice(1)
  const name = getEntityName(primary, t("projectsCommand.noName"))

  return (
    <Flex align="center" gap={8} className="min-w-0">
      <PersonAvatar person={primary} />
      <Text className="min-w-0 text-slate-800" ellipsis={{ tooltip: name }}>
        {name}
      </Text>
      {rest.length ? (
        <Popover
          trigger={["click", "hover", "focus"]}
          title={t("projectsCommand.roles.title")}
          content={
            <Flex vertical gap={8} className="min-w-48">
              {rest.map(({ person, role }) => (
                <Flex key={`${role}-${person.id}`} align="center" gap={8}>
                  <PersonAvatar person={person} />
                  <Flex vertical className="min-w-0">
                    <Text>
                      {getEntityName(person, t("projectsCommand.noName"))}
                    </Text>
                    <Text type="secondary" className="text-xs">
                      {role}
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          }
        >
          <button
            type="button"
            className="shrink-0 rounded-full bg-teal-50 px-2 text-xs font-medium text-teal-800 leading-5 cursor-pointer border-0 focus-visible:outline-2 focus-visible:outline-teal-700"
            aria-label={t("projectsCommand.roles.more", { count: rest.length })}
          >
            +{rest.length}
          </button>
        </Popover>
      ) : null}
    </Flex>
  )
}

/** "$8.2K": the overrun line is a signal, not the ledger. */
export const compactCurrency = (value: string | number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1
  }).format(Number(value))

export function FinancialCell({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation()
  const price = project.sales_price
  const hasPrice = price !== null && price !== undefined && price !== ""

  return (
    <Flex vertical align="end" className="min-w-0 tabular-nums">
      <Text className={hasPrice ? "text-slate-800" : "text-slate-500"}>
        {hasPrice ? budgetFormat(price) : t("projectsCommand.notAvailable")}
      </Text>
      {project.financial_exception === "over_budget" ? (
        <Text className="whitespace-nowrap text-[13px] text-red-700">
          {project.over_budget_amount
            ? t("projectsCommand.financial.overBudgetBy", {
                amount: compactCurrency(project.over_budget_amount)
              })
            : t("projectsCommand.financial.overBudget")}
        </Text>
      ) : null}
    </Flex>
  )
}

function normalizeProgress(project: ProjectListItem): number | null {
  return project.progress == null ? null : normalizePercent(project.progress)
}

export function ProgressCell({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation()
  const percent = normalizeProgress(project)

  // No milestones means progress cannot be measured: say so instead of
  // drawing an empty bar at 0%.
  if (percent === null) {
    return (
      <Text type="secondary">
        {t("projectsCommand.progress.notConfigured")}
      </Text>
    )
  }
  if (
    percent === 0 &&
    (NOT_STARTED_STATUSES.has(project.project_status) ||
      project.schedule_health?.state === "not_started")
  ) {
    return (
      <Text type="secondary">{t("projectsCommand.progress.notStarted")}</Text>
    )
  }

  return (
    <Flex align="center" gap={8} className="min-w-0">
      <Progress
        percent={percent}
        showInfo={false}
        size="small"
        strokeColor="var(--ant-color-primary, #2A7D8C)"
        className="m-0 min-w-0 flex-1"
        aria-label={t("projectsCommand.progress.aria", { percent })}
      />
      <Text className="w-9 shrink-0 text-end tabular-nums text-slate-700">
        {percent}%
      </Text>
    </Flex>
  )
}

export function LastActivityCell({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation()
  const { readUpdated, formatDateTime, dayOf } = useDateTimeFormat()
  const at = readUpdated(project.updated)
  const relative = shortRelativeTime(at)
  if (!at || !relative) {
    return <Text type="secondary">{t("projectsCommand.notAvailable")}</Text>
  }

  const days = dayOf(dayjs()).diff(dayOf(at), "day")
  const label =
    days <= 0
      ? t("projectsCommand.activity.today")
      : days === 1
        ? t("projectsCommand.activity.yesterday")
        : relative
  const stale =
    !!project.activity_stale && !isArchivingStatus(project.project_status)

  return (
    <Tooltip title={formatDateTime(at)}>
      <Flex vertical className="min-w-0" tabIndex={0}>
        <Text
          className={stale ? "text-amber-800 font-medium" : "text-slate-700"}
        >
          {label}
        </Text>
        {stale ? (
          <Text className="text-amber-800 text-xs whitespace-nowrap">
            {t("projectsCommand.activity.stale", { count: days })}
          </Text>
        ) : null}
      </Flex>
    </Tooltip>
  )
}

// `updated` arrives rendered in the account's own format and zone, so it is read
// in that format, never by a bare `dayjs(value)` (month-first).
function useDateTimeFormat() {
  const { userFormat } = useDateFormat()
  const { timezone, dateFormat } = useDateFormatter()
  return {
    readUpdated: (value: unknown) =>
      readAccountInstant(value, dateFormat, timezone),
    dayOf: (at: Dayjs) => at.tz(timezone).startOf("day"),
    formatDateTime: (at: Dayjs) =>
      at.tz(timezone).format(`${userFormat || "MMM D, YYYY"} h:mm A`)
  }
}

/** What the row could not fit on a narrower table, under the row itself. */
export function ProjectRowDetails({
  project,
  showFinancial,
  showActivity
}: {
  project: ProjectListItem
  showFinancial: boolean
  showActivity: boolean
}) {
  const { t } = useTranslation()
  return (
    <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-x-8 gap-y-3 py-1 ps-10">
      {showFinancial ? (
        <div className="flex flex-col gap-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t("projectsCommand.columns.financial")}
          </dt>
          <dd className="m-0 [&_.ant-flex]:!items-start">
            <FinancialCell project={project} />
          </dd>
        </div>
      ) : null}
      {showActivity ? (
        <div className="flex flex-col gap-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t("projectsCommand.columns.lastActivity")}
          </dt>
          <dd className="m-0">
            <LastActivityCell project={project} />
          </dd>
        </div>
      ) : null}
    </dl>
  )
}
